import {
  Flag,
  countryName,
  digitsOf,
  formatRange,
  formatWhole,
  type Money,
  type MoneyRange,
  type Plan,
  type PlanCountry,
  type PlanStop,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { animalOf, calendarOf, dateIn, weekendOf, yearIn } from '../../lib/localCalendar';

import { CrossingTask, isForm, isVisa } from './CrossingTask';
import {
  bookedNights,
  dayRange,
  offsetMinutes,
  plusDays,
  utcOffset,
  type CountryView,
} from '../../lib/countryFacts';
import { dayShort } from '../../lib/format';
import { PlugIcon } from '../../components/icons/PlugIcon';

interface Props {
  plan: Plan;
  row: PlanCountry;
  view: CountryView;
  /** The passport travelled on; none when not set, or the Docs not had. */
  passport: string | null;
  /** A day's lowest and highest, as shares of the scale. */
  band: { min: number; max: number };
  scale: number;
  open: boolean;
  onToggle: () => void;
  /** A stop's beds pressed. */
  onStop: (stop: PlanStop) => void;
}

const pct = (share: number) => `${String(share * 100)}%`;

/** The tags of a country's row: what its entry asks, and its nights booked of all. */
function EntryTags({ view }: { view: CountryView }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [crossing] = view.crossings;
  const rule = crossing?.rule ?? null;
  const tone = view.booked === 0 ? 'warn' : view.booked >= view.nights ? 'ok' : 'info';
  return (
    <span className="countries-tags">
      {crossing && rule && (
        <>
          {isVisa(crossing) && crossing.due !== null && !crossing.done ? (
            <span className="state-tag state-tag-alert">
              {t('countries.rule.by', {
                what: t(`countries.rule.${rule.kind}`),
                day: dayShort(crossing.due, lang),
              })}
            </span>
          ) : (
            <span
              className={`state-tag ${rule.kind === 'arrival' ? 'state-tag-plain' : 'state-tag-ok'}`}
            >
              {t(`countries.rule.${rule.kind}`, { days: rule.days })}
            </span>
          )}
          {isForm(crossing) && (
            <span className={`state-tag ${crossing.done ? 'state-tag-ok' : 'state-tag-warn'}`}>
              {rule.form}
            </span>
          )}
        </>
      )}
      {view.nights > 0 && (
        <span className={`state-tag state-tag-${tone}`}>
          {t('countries.bookedOf', { booked: view.booked, nights: view.nights })}
        </span>
      )}
    </span>
  );
}

/** Entry and stay: what the passport meets, when in and out, the days used and what to do. */
/** Where a citizen of one moves freely: the EU, the EEA and Switzerland. */
const FREE = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'CH',
]);

/** Whether a passport of one country moves freely in another: both of the EU, EEA or Switzerland. */
export function freeToMove(passport: string, country: string): boolean {
  return FREE.has(passport) && FREE.has(country);
}

function EntryPanel({
  view,
  passport,
  trip,
}: {
  view: CountryView;
  passport: string | null;
  trip: string;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <section className="countries-panel">
      <h3>{t('countries.entry')}</h3>
      {passport === null ? (
        <p>
          <Link to={`/trips/${trip}/docs`}>{t('countries.setPassport')}</Link>
        </p>
      ) : (
        <dl>
          <div>
            <dt>{t('countries.passport', { country: countryName(passport, lang) })}</dt>
            <dd>
              <strong>
                {view.country === passport
                  ? t('countries.ownCountry')
                  : freeToMove(passport, view.country)
                    ? t('countries.freeMovement')
                    : view.crossings[0]?.rule
                      ? t(`countries.ruleLong.${view.crossings[0].rule.kind}`, {
                          days: view.crossings[0].rule.days,
                        })
                      : t('countries.unknownRule')}
              </strong>
            </dd>
          </div>
        </dl>
      )}
      {passport !== null &&
        view.crossings.map((crossing) => {
          const rule = crossing.rule;
          return (
            <div key={crossing.entry} className="countries-used">
              {rule && rule.days > 0 && (
                <>
                  <dl>
                    <div>
                      <dt>{t('countries.daysUsed')}</dt>
                      <dd className="mono">
                        {t('countries.daysOf', { days: crossing.days, max: rule.days })}
                      </dd>
                    </div>
                  </dl>
                  <span
                    className={`countries-meter${crossing.days > rule.days ? ' countries-meter-over' : ''}`}
                    aria-hidden="true"
                  >
                    <span style={{ width: pct(Math.min(1, crossing.days / rule.days)) }} />
                  </span>
                </>
              )}
              {rule && crossing.due !== null && (
                <p className="countries-task">
                  <CrossingTask crossing={crossing} />{' '}
                  {isVisa(crossing)
                    ? t('countries.applyBy', {
                        form: rule.form,
                        day: dayShort(crossing.due, lang),
                      })
                    : t('countries.formWindow', {
                        form: rule.form,
                        range: dayRange(crossing.due, crossing.border, lang),
                      })}
                </p>
              )}
              {rule && rule.source !== 'own' && (
                <a href={rule.url} target="_blank" rel="noreferrer">
                  {t('countries.official')} ↗
                </a>
              )}
            </div>
          );
        })}
    </section>
  );
}

/** Money: the currency and its rate, what each kind of spending comes to, paid and to pay. */
/**
 * A currency's notes or coins in hand, smallest first, each in its own money: `20 ฿`, a coin of
 * 25 satang `0,25 ฿`; those seldom seen after them, paler, said so when pointed at. Nothing when
 * none is known.
 */
function Denominations({
  label,
  currency,
  often,
  seldom,
}: {
  label: string;
  currency: string;
  often: number[];
  seldom: number[];
}) {
  const { t, i18n } = useTranslation();
  if (often.length + seldom.length === 0) return null;
  const said = new Intl.NumberFormat(i18n.language === 'et' ? 'et-EE' : 'en-GB', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return (
    <div className="countries-denominations">
      <dt>{label}</dt>
      <dd>
        {often.map((amount) => (
          <span key={amount} className="denomination mono">
            {said.format(amount)}
          </span>
        ))}
        {seldom.map((amount) => (
          <span
            key={amount}
            className="denomination denomination-rare mono"
            title={t('countries.seldom')}
          >
            {said.format(amount)}
            <span className="visually-hidden"> · {t('countries.seldom')}</span>
          </span>
        ))}
      </dd>
    </div>
  );
}

function MoneyPanel({ view }: { view: CountryView }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const facts = view.facts;
  // The amounts in the trip's money, or in the country's at today's rate when asked.
  const [local, setLocal] = useState(false);
  const trip = view.paid.currency;
  const there = facts?.currency && facts.currency !== trip && facts.rate ? facts : null;
  const shown = (money: Money): Money =>
    local && there?.currency && there.rate
      ? {
          currency: there.currency,
          minor: Math.round(
            (money.minor / 10 ** digitsOf(trip)) *
              (there.rate.perTrip / 1e9) *
              10 ** digitsOf(there.currency),
          ),
        }
      : money;
  const range = (said: MoneyRange): MoneyRange => ({
    min: shown(said.min),
    max: shown(said.max),
  });
  const kinds: [string, MoneyRange][] = [
    ['stays', range(view.stays)],
    ['food', range(view.food)],
    ['local', range(view.local)],
    ['activities', range(view.activities)],
  ];
  // Only what costs something there: a country only changed in says none.
  const costing = kinds.filter(([, range]) => range.max.minor > 0);
  const top = Math.max(1, ...kinds.map(([, range]) => range.max.minor));
  return (
    <section className="countries-panel">
      <div className="countries-panel-head">
        <h3>{t('countries.money')}</h3>
        {there?.currency && (
          <div
            className="segmented segmented-small"
            role="group"
            aria-label={t('countries.inMoney')}
          >
            {[trip, there.currency].map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={local === (code !== trip)}
                onClick={() => {
                  setLocal(code !== trip);
                }}
              >
                {code}
              </button>
            ))}
          </div>
        )}
      </div>
      <dl>
        {facts?.currency && (
          <div>
            <dt>{t('countries.currency')}</dt>
            <dd>
              <strong>{currencyName(facts.currency, lang)}</strong> {facts.currency}
            </dd>
          </div>
        )}
        {/* No rate where the money is the trip's own: a euro is a euro. */}
        {facts?.currency && facts.currency !== view.paid.currency && (
          <div>
            <dt>{t('countries.rate')}</dt>
            <dd className="mono">
              {facts.rate
                ? rateSaid(facts.rate.perTrip, view.paid.currency, facts.currency, lang)
                : t('countries.noRate')}
            </dd>
          </div>
        )}
        {facts?.currency && facts.money && (
          <>
            <Denominations
              label={t('countries.notes')}
              currency={facts.currency}
              often={facts.money.notes}
              seldom={facts.money.rareNotes}
            />
            <Denominations
              label={t('countries.coins')}
              currency={facts.currency}
              often={facts.money.coins}
              seldom={facts.money.rareCoins}
            />
          </>
        )}
      </dl>
      {costing.length > 0 && (
        <dl className="countries-kinds">
          {costing.map(([kind, range]) => (
            <div key={kind}>
              <dt>{t(`countries.${kind}`)}</dt>
              <span className="countries-meter" aria-hidden="true">
                <span style={{ width: pct(range.max.minor / top) }} />
              </span>
              <dd className="mono">{formatRange(range, lang, true)}</dd>
            </div>
          ))}
        </dl>
      )}
      {view.paid.minor + view.toPay.minor > 0 && (
        <dl>
          <div>
            <dt>{t('countries.paidToPay')}</dt>
            <dd className="mono">
              {formatWhole(shown(view.paid), lang)} · {formatWhole(shown(view.toPay), lang)}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}

/** A currency by its name in the reader's language: `Thai baht`. */
function currencyName(code: string, lang: string): string {
  // A code no language names, as itself.
  return new Intl.DisplayNames([lang], { type: 'currency', fallback: 'none' }).of(code) ?? code;
}

/** `€1 = ฿36.5 · ฿100 = €2.74`: the rate both ways, in round amounts. */
function rateSaid(perTrip: number, trip: string, local: string, lang: string): string {
  const per = perTrip / 1e9;
  const money = (amount: number, currency: string) =>
    new Intl.NumberFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumSignificantDigits: 3,
    }).format(amount);
  // The local side in a round amount just over a unit's worth: 100 baht, 100 000 dong, $10.
  const round = 10 ** Math.max(1, Math.ceil(Math.log10(per)));
  return `${money(1, trip)} = ${money(per, local)} · ${money(round, local)} = ${money(round / per, trip)}`;
}

/** Nights, the days kept while there, the time and the plugs. */
/** Where the nights are spent: each stop's, a bed booked for them or not. */
function StayPanel({ view, onStop }: { view: CountryView; onStop: (stop: PlanStop) => void }) {
  const { t } = useTranslation();
  return (
    <section className="countries-panel">
      <h3>{t('countries.nightsTitle')}</h3>
      <NightsList stops={view.stops} onStop={onStop} />
    </section>
  );
}

/** A public holiday's mark, a flag; a day only marked, a ribbon. */
function HolidayMark({ public: isPublic }: { public: boolean }) {
  return (
    <svg
      className={isPublic ? 'holiday-mark holiday-public' : 'holiday-mark'}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path
        d={
          isPublic
            ? 'M5 21V4M5 4h11l-2 4 2 4H5'
            : 'M12 3l2.6 5.6 6 .7-4.5 4.1 1.2 6L12 16.4 6.7 19.4l1.2-6L3.4 9.3l6-.7z'
        }
      />
    </svg>
  );
}

/** What it is like there: the holidays while there, its time against home, its plugs. */
function TherePanel({ view, trip }: { view: CountryView; trip: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const home = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Each clock the country keeps where the trip is, its towns by it: Sydney at +11 in summer,
  // Cairns at +10; each town's at its own days, summer time and all.
  const clocks = new Map<number, string[]>();
  for (const stop of view.stops) {
    if (stop.zone === null) continue;
    const offset = offsetMinutes(stop.zone, stop.start);
    const towns = clocks.get(offset) ?? [];
    if (!towns.includes(stop.city)) towns.push(stop.city);
    clocks.set(offset, towns);
  }
  const facts = view.facts;
  const weekday = (day: string) =>
    new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
      weekday: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${day}T00:00:00Z`));
  return (
    <section className="countries-panel">
      {/* The holidays while there beside the days they are about; none, nothing said. */}
      {facts && facts.holidays.length > 0 && (
        <>
          <h3>
            {t('countries.whileThere')}{' '}
            <span className="countries-holiday-days">
              · {dayRange(view.first, view.last, lang)}
            </span>
          </h3>
          <ul className="countries-holidays">
            {facts.holidays.map((holiday) => {
              const kind = t(
                holiday.public ? 'countries.publicHoliday' : 'countries.observance',
              );
              return (
                <li key={`${holiday.day}-${holiday.name}`} title={kind}>
                  <span className="mono">
                    {weekday(holiday.day)} {dayShort(holiday.day, lang)}
                  </span>
                  <span
                    className={holiday.public ? 'holiday-name holiday-public' : 'holiday-name'}
                  >
                    <HolidayMark public={holiday.public} />
                    {holiday.name}
                    <span className="visually-hidden"> · {kind}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <dl>
        {clocks.size > 0 && (
          <div className="countries-time">
            <dt className="countries-time-head">
              {t('countries.time')}
              {/* Home's working hours on these clocks, on a page of their own. */}
              <Link to={`/trips/${trip}/clocks?country=${view.country}`}>
                {t('clocks.open')} →
              </Link>
            </dt>
            <dd>
              {[...clocks].map(([offset, towns]) => (
                <Clock
                  key={offset}
                  there={offset}
                  home={offsetMinutes(home, view.first)}
                  towns={clocks.size > 1 ? towns : []}
                />
              ))}
            </dd>
          </div>
        )}
        <LocalCalendarRows country={view.country} day={view.first} />
        {facts && (
          <div>
            <dt>{t('countries.plugs')}</dt>
            <dd className="countries-plugs">
              {facts.plugs === '' && facts.volts === null ? (
                t('countries.unknown')
              ) : (
                <>
                  {/* Each socket by its face and its letter, then the voltage. */}
                  {facts.plugs.split('').map((letter) => (
                    <span
                      key={letter}
                      className="plug"
                      title={t('countries.plugType', { letter })}
                    >
                      <PlugIcon letter={letter} />
                      <span className="mono">{letter}</span>
                    </span>
                  ))}
                  {facts.volts !== null && (
                    <span className="mono">
                      {[`${String(facts.volts)} V`, ...hertz(facts.hertz)].join(' · ')}
                    </span>
                  )}
                </>
              )}
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

/** The mains frequency, `50 Hz` or Japan's `50/60 Hz`; nothing when not said. */
function hertz(cycles: number[]): string[] {
  return cycles.length === 0 ? [] : [`${cycles.join('/')} Hz`];
}

/**
 * A clock the country keeps: its offset from UTC, how far it is from home by a house and the
 * hours (`⌂ +4 h`, said in full when pointed at), and its towns when the country keeps more than
 * one; at home's own time the offset alone.
 */
function Clock({ there, home, towns }: { there: number; home: number; towns: string[] }) {
  const { t } = useTranslation();
  const diff = (there - home) / 60;
  const hours = Math.abs(diff);
  const said = t(diff > 0 ? 'countries.homeAhead' : 'countries.homeBehind', { count: hours });
  return (
    <div className="clock">
      <div className="clock-line">
        <span className="clock-offset mono">{utcOffset(there)}</span>
        {diff !== 0 && (
          <span className="clock-home" title={said}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 10.5 12 3l9 7.5V21H3zM9.5 21v-6h5v6" />
            </svg>
            <span className="mono" aria-hidden="true">
              {diff > 0 ? '+' : '−'}
              {String(hours)} h
            </span>
            <span className="visually-hidden">{said}</span>
          </span>
        )}
      </div>
      {towns.length > 0 && <div className="clock-towns quiet">{towns.join(', ')}</div>}
    </div>
  );
}

/**
 * Each stop's nights in order, a bed booked for them or not: its town and dates, and whether
 * its nights are booked — a stop passed with no night has none to book and is left out.
 */
export function NightsList({
  stops,
  onStop,
}: {
  stops: PlanStop[];
  /** A stop pressed: its beds booked, to change. */
  onStop: (stop: PlanStop) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const stayed = stops
    .filter((stop) => stop.days > 0)
    .toSorted((a, b) => a.start.localeCompare(b.start));
  return (
    <ul className="countries-nights">
      {stayed.map((stop) => {
        const booked = bookedNights(stop);
        const open = stop.days - booked;
        const said =
          open === 0
            ? t('countries.booked')
            : booked === 0
              ? t('countries.notBooked')
              : t('countries.partly', { booked, nights: stop.days });
        return (
          <li key={stop.id}>
            <button
              type="button"
              className="countries-night-stop"
              aria-label={t('stays.open', { city: stop.city })}
              onClick={() => {
                onStop(stop);
              }}
            >
              {stop.city} ·{' '}
              <span className="mono">
                {dayRange(stop.start, plusDays(stop.start, stop.days), lang)}
              </span>
            </button>
            {/* Booked a green tick; a bed to find in the warning's colour, how many of its
                nights are booked when some are; said in full when pointed at. */}
            <span
              className={`state-tag ${open === 0 ? 'state-tag-ok' : booked === 0 ? 'state-tag-warn' : 'state-tag-info'}`}
              title={said}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* After Lucide's check and bed (ISC licence). */}
                <path
                  d={
                    open === 0
                      ? 'M20 6 9 17l-5-5'
                      : 'M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9'
                  }
                />
              </svg>
              {booked > 0 && open > 0 && (
                <span
                  className="mono"
                  aria-hidden="true"
                >{`${String(booked)}/${String(stop.days)}`}</span>
              )}
              <span className="visually-hidden">{said}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A country's own calendar beside ours, when it keeps one: its name, this year in it — Thailand's
 * 2569 is our 2026 — and the trip's first day there as written there; its weekend when it is not
 * Saturday and Sunday.
 */
function LocalCalendarRows({ country, day }: { country: string; day: string }) {
  const { t, i18n } = useTranslation();
  const own = calendarOf(country);
  const weekend = weekendOf(country);
  const names = new Intl.DateTimeFormat(i18n.language === 'et' ? 'et-EE' : 'en-GB', {
    weekday: 'long',
    timeZone: 'UTC',
  });
  return (
    <>
      {own && (
        <>
          <div>
            <dt>{t('local.calendar')}</dt>
            <dd>
              {t(`local.system.${own.system}`)}
              {own.religious && <span className="quiet"> · {t('local.religious')}</span>}
            </dd>
          </div>
          <div>
            <dt>{t('local.year')}</dt>
            <dd className="mono">
              {own.lunar
                ? t('local.animalYear', {
                    animal: t(`local.animal.${String(animalOf(day, own.system))}`),
                  })
                : t('local.yearIs', { local: yearIn(day, own.system), ours: day.slice(0, 4) })}
            </dd>
          </div>
          <div>
            <dt>{t('local.date')}</dt>
            <dd lang={own.locale}>{dateIn(day, own.system, own.locale)}</dd>
          </div>
        </>
      )}
      {weekend.join() !== '6,7' && (
        <div>
          <dt>{t('local.weekend')}</dt>
          <dd>{weekend.map((one) => names.format(Date.UTC(2026, 0, 4 + one))).join(', ')}</dd>
        </div>
      )}
    </>
  );
}

/** A country's row, and under it when open, its entry, money and stay. */
export function CountryRow({
  plan,
  row,
  view,
  passport,
  band,
  scale,
  open,
  onToggle,
  onStop,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const name = countryName(row.country, lang);
  // Its stops where a day is dear, named when its day's cost is pointed at.
  const dear = plan.stops
    .filter((s) => s.country === row.country && s.status === 'expensive')
    .map((s) => s.city);
  return (
    <tbody className={open ? 'countries-open' : undefined}>
      <tr className={dear.length > 0 ? 'dear' : undefined}>
        <th scope="row">
          {/* The whole name pressed opens it or closes it, not its arrow alone. */}
          <button
            type="button"
            className="countries-toggle"
            aria-expanded={open}
            aria-label={t(open ? 'countries.close' : 'countries.open', { country: name })}
            onClick={onToggle}
          >
            <span className="countries-arrow" aria-hidden="true">
              {open ? '▾' : '▸'}
            </span>
            <span className="countries-name">
              <span>
                <Flag country={row.country} />
                {name}
              </span>
              <span className="quiet countries-where">
                {dayRange(view.first, view.last, lang)}
                {view.stops.length > 0 &&
                  ` · ${view.stops.map((stop) => stop.city).join(', ')}`}
              </span>
            </span>
          </button>
        </th>
        <td className="mono">{row.days}</td>
        <td>
          <span className="day-bar">
            <span className="day-track" aria-hidden="true">
              <span
                className="day-range"
                style={{ left: pct(band.min), width: pct(band.max - band.min) }}
              />
              {row.expectedPerDay && (
                <span
                  className="day-dot"
                  style={{ left: pct(row.expectedPerDay.minor / scale) }}
                />
              )}
            </span>
            <span
              className="mono"
              title={
                dear.length > 0
                  ? t('countries.dearAt', {
                      over: formatWhole(plan.totals.dearPerDay, lang),
                      stops: dear.join(', '),
                    })
                  : undefined
              }
            >
              {row.expectedPerDay ? formatWhole(row.expectedPerDay, lang) : '—'}
              {dear.length > 0 && (
                <span className="visually-hidden">
                  {' '}
                  {t('countries.dearAt', {
                    over: formatWhole(plan.totals.dearPerDay, lang),
                    stops: dear.join(', '),
                  })}
                </span>
              )}
            </span>
          </span>
        </td>
        <td className="mono">{formatRange(row.total, lang, true)}</td>
        <td>
          <EntryTags view={view} />
        </td>
      </tr>
      {open && (
        <tr className="countries-detail">
          <td colSpan={5}>
            <div className="countries-panels">
              <EntryPanel view={view} passport={passport} trip={plan.trip.id} />
              <MoneyPanel view={view} />
              {view.nights > 0 && <StayPanel view={view} onStop={onStop} />}
              <TherePanel view={view} trip={plan.trip.id} />
            </div>
          </td>
        </tr>
      )}
    </tbody>
  );
}
